import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import TabViewController from './TabViewController';
import DocumentDetailView from './detail/DocumentDetailViewController';
import QualityAlertViewController from './detail/qualityalert/QualityAlertViewController';
import PdfSplitterView from './documents/splitter/PdfSplitterViewController';
import { DocumentUploadsProvider } from './documents/DocumentUploadsContext';
import ConsultationDetailViewController from './scribe/ConsultationDetailViewController';
import WorkflowDetailView from './workflow/WorkflowDetailView';
import PatientViewController from './patients/PatientViewController';
import { LoginViewController } from '@medprompt/shared';
import SignUpViewController from './SignUpViewController';
import { CodeVerifyController } from '@medprompt/shared';
import OAuthCallbackController from './OAuthCallbackController';
import { NotificationProvider, NotificationStack, ConnectionStatusBanner, useNotifications, DocumentViewerProvider, TaskViewerProvider, useDocumentViewers, useTaskViewers, BugReportContextProvider } from '@medprompt/shared';
import { AuthProvider, useAuth } from '@medprompt/shared';
import ProtectedRoute from './ProtectedRoute';
import FloatingSupportButton from './FloatingSupportButton';
import { InsightsService, sharedApiClient } from './fetcher';
import './App.css';
import '@medprompt/shared/src/common/TabPageTitle.css';
import { MAIN_TAB_PAGE_LABEL } from '@medprompt/shared';

// Owns the update stream lifecycle - must be inside both AuthProvider and NotificationProvider
const UpdateStreamInitializer = () => {
  const { manager } = useNotifications();
  const { user } = useAuth();

  const userId = user?.userId;

  const [clinicTrigger, setClinicTrigger] = useState(0);

  useEffect(() => {
    const onClinicChanged = () => setClinicTrigger(prev => prev + 1);
    window.addEventListener('clinicChanged', onClinicChanged);
    return () => window.removeEventListener('clinicChanged', onClinicChanged);
  }, []);

  useEffect(() => {
    if (!manager) {
      return;
    }

    if (!userId) {
      manager.disconnectUpdateStream();
      return;
    }

    manager.resetUpdateStream(sharedApiClient);

    // NotificationContext calls this when a backgrounded tab returns to a dead connection.
    window.reconnectUpdateStream = (notificationManager) => {
      notificationManager.resetUpdateStream(sharedApiClient);
    };
  }, [manager, userId, clinicTrigger]);

  return null;
};

// Component to initialize DocumentViewerProvider and TaskViewerProvider with required services
const AppWithViewerProviders = ({ children }) => {
  const { manager } = useNotifications();
  const { user } = useAuth();
  // Stable identity: the viewer providers key their fetch callbacks off this.
  const insightsService = useMemo(() => new InsightsService(sharedApiClient), []);
  
  return (
    <DocumentViewerProvider notificationManager={manager} insightsService={insightsService} user={user}>
      <TaskViewerProvider notificationManager={manager} insightsService={insightsService} user={user}>
        {children}
      </TaskViewerProvider>
    </DocumentViewerProvider>
  );
};

// Wrapper for TabViewController to inject viewer contexts as props
const TabViewControllerWithViewers = ({ setActiveTabLabel }) => {
  const documentViewerContext = useDocumentViewers();
  const taskViewerContext = useTaskViewers();
  return <TabViewController setActiveTabLabel={setActiveTabLabel} documentViewerContext={documentViewerContext} taskViewerContext={taskViewerContext} />;
};

// Wrapper for DocumentDetailView to inject viewer context as props
const DocumentDetailViewWithViewers = () => {
  const viewerContext = useDocumentViewers();
  return <DocumentDetailView viewerContext={viewerContext} />;
};

function App() {
  const [activeTabLabel, setActiveTabLabel] = useState(MAIN_TAB_PAGE_LABEL.documentsList);

  return (
    <NotificationProvider>
      <Router>
        <BugReportContextProvider>
          <AuthProvider>
            <UpdateStreamInitializer />
            <AppWithViewerProviders>
              <DocumentUploadsProvider>
                <div className="App">
                <Routes>
                  <Route path="/login" element={<LoginViewController />} />
                  <Route path="/signup" element={<SignUpViewController />} />
                  <Route path="/verify" element={<CodeVerifyController />} />
                  <Route path="/callback" element={<OAuthCallbackController />} />
                  <Route path="/" element={
                    <ProtectedRoute>
                      <TabViewControllerWithViewers setActiveTabLabel={setActiveTabLabel} />
                    </ProtectedRoute>
                  } />
                  <Route path="/document-detail/:documentId" element={
                    <ProtectedRoute>
                      <DocumentDetailViewWithViewers />
                    </ProtectedRoute>
                  } />
                  <Route path="/document-detail/:documentId/quality-alert" element={
                    <ProtectedRoute>
                      <QualityAlertViewController />
                    </ProtectedRoute>
                  } />
                  <Route path="/split-pdf" element={
                    <ProtectedRoute>
                      <PdfSplitterView />
                    </ProtectedRoute>
                  } />
                  <Route path="/split-pdf/:documentId" element={
                    <ProtectedRoute>
                      <PdfSplitterView />
                    </ProtectedRoute>
                  } />
                  <Route path="/consultation-detail/:consultationId" element={
                    <ProtectedRoute>
                      <ConsultationDetailViewController />
                    </ProtectedRoute>
                  } />
                  <Route path="/workflows" element={
                    <ProtectedRoute>
                      <TabViewControllerWithViewers setActiveTabLabel={setActiveTabLabel} />
                    </ProtectedRoute>
                  } />
                  <Route path="/workflow/:ruleId" element={
                    <ProtectedRoute>
                      <WorkflowDetailView />
                    </ProtectedRoute>
                  } />
                  <Route path="/patient/:nhsId" element={
                    <ProtectedRoute>
                      <PatientViewController />
                    </ProtectedRoute>
                  } />
                </Routes>
                <FloatingSupportButton activeTabLabel={activeTabLabel} />
                <ConnectionStatusBanner />
                <NotificationStack />
                <div className="version-footer">
                  Version: {process.env.REACT_APP_BUILD_VERSION || 'unspecified'}
                </div>
                </div>
              </DocumentUploadsProvider>
            </AppWithViewerProviders>
          </AuthProvider>
        </BugReportContextProvider>
      </Router>
    </NotificationProvider>
  );
}

export default App;